import { useEffect, useRef } from "react";

import { useEntityActions } from "./useEntityActions";
import { RootState, useAppDispatch, useAppSelector } from "../Redux";
import { EntityService } from "../Services";
import { ActionStatusHelper, RecordMap } from "@/Shared";
import { PollerOptions, } from "@/Shared/Logic/Pollers";
import { EntityMode, EntityState, Entity, ActionResponse, EntityActionsRedux, EntityHook, ExportParams, DomainType } from "@/Shared/Types";
import { useEntityPoller } from "./useEntityPoller";
import { ExportFormat } from "@/Domains";

const fetchingDomains : Set<string> = new Set();

/**
 * =====================================================
 * makeEntityHook
 * =====================================================
 *
 * High-level hook factory to manage entity state and actions.
 * Provides a hook that combines:
 * - Redux slice selection for entity state
 * - Async API calls via EntityService
 * - Polling support for automatic updates
 * - Smart cache-first actions and store-only updates
 *
 * Author: Samano CASTRE
 * Date: February 01, 2026
 */
export function makeEntityHook<T extends Entity>(
	selectSlice: (state: RootState) => EntityState<T>,
	actionsRedux: EntityActionsRedux<T>,
	entityService: EntityService<T>,
	domainType : string,
	pollerOptions?: PollerOptions
): () => EntityHook<T> {


	/**
	 * =====================================================
	 * useEntity hook
	 * =====================================================
	 *
	 * Provides entity data, status, and action methods.
	 * Handles:
	 * - State initialization and auto-fetching
	 * - Online/offline handling
	 * - Polling for new data
	 * - CRUD and store-only actions
	 */
	return function useEntity<T>() {

		const dispatch = useAppDispatch();
		const isOnline = !!(useAppSelector(state => state.sessionState.userSession)?.id);

		/** Responses and executor from the generic entity actions hook */
		const {
			responses,
			execute,
			setResponse,
			queryId
		} = useEntityActions<any>(selectSlice, actionsRedux, entityService, pollerOptions?.pollerKey);

		/** Entity state and mode */
		const entityState = useAppSelector(selectSlice);
		const curEntityMode = entityState.mode;
		const entityModeState = entityState[curEntityMode];
		const entityModeStateStatus = entityModeState?.status ?? 'idle';

		/** Determine if state or hook is idle */
		const isStateIdle = ActionStatusHelper.isIdle(entityModeStateStatus);
		const isHookIdle = ActionStatusHelper.isIdle(responses.fetchAllEntities.status);

		/** Determine if initial fetch should run */
		const shouldInit = isOnline && isStateIdle && isHookIdle && !!entityService.fetchAll;
		const hasInitializedRef = useRef(false);

		/** =====================================================
		 * Poller setup
		 * =====================================================
		 */
		useEntityPoller(
			isOnline,
			ActionStatusHelper.isSucceeded(entityModeStateStatus)
			&& !!entityService.fetchSinceDate,
			execute,
			pollerOptions
		);

		/** =====================================================
		 * Initial fetch effect
		 * =====================================================
		 *
		 * Automatically fetches all entities when state is idle.
		 * Clears data when offline.
		 */
		useEffect(() => {

			if (!entityModeState || (shouldInit && !hasInitializedRef.current)) {
				if(fetchingDomains.has(domainType)) {
					return;
				}
				fetchingDomains.add(domainType);
				execute("findAllEntities");
				hasInitializedRef.current = true;
			}
			else if (!isOnline && !isStateIdle) {
				execute("clearData");
				hasInitializedRef.current = false;
				fetchingDomains.delete(domainType);
			}

		}, [isOnline, isStateIdle, shouldInit, entityModeState]);


		/** =====================================================
		 * Returned API
		 * =====================================================
		 *
		 * Exposes:
		 * - Responses and status
		 * - Redux slice state & error
		 * - Data in memory
		 * - API-only, store-only, and smart actions
		 */
		return {
			queryId,
			/** In-memory store data */
			dataMap: new RecordMap(entityModeState?.keyPairData ?? {}),

			createAction: {
				response: responses.createEntity,
				setResponse: (response: ActionResponse) => setResponse('createEntity', response),
				execute: entityService.create
					? (payload: any, idempotencyKey? : string) => execute('createEntity', {values : payload, idempotencyKey})
					: undefined
			},
			updateAction: {
				response: responses.updateEntity,
				setResponse: (response: ActionResponse) => setResponse('updateEntity', response),
				execute: entityService.update
					? (payload: any, idempotencyKey? : string) => execute('updateEntity', payload)
					: undefined,
			},

			deleteAction: {
				response: responses.deleteEntity,
				setResponse: (response: ActionResponse) => setResponse('deleteEntity', response),
				execute: entityService.delete
					? (id: number | string, canRestore?: boolean) => execute('deleteEntity', { id, canRestore })
					: undefined,
			},

			restoreAction: {
				response: responses.restoreEntity,
				setResponse: (response: ActionResponse) => setResponse('restoreEntity', response),
				execute: entityService.restore
					? (payload: any) => execute('restoreEntity', payload)
					: undefined,
			},

			fetchAction: {
				response: responses.fetchEntity,
				execute: entityService.fetchOne
					? (id: number | string) => execute('fetchEntity', id)
					: undefined,
			},

			fetchListAction: {
				response: responses.fetchAllEntities,
				execute: entityService.fetchAll
					? (mode?: EntityMode) => execute('fetchAllEntities', mode)
					: undefined,
			},

			findAction: {
				response: responses.findEntity,
				execute: (id: number | string) => execute('findEntity', id),
			},

			searchAction: {
				response: responses.search,
				execute: (params: ExportParams<any>) => execute('search', params),
			},

			exportAction: {
				response: responses.export,
				setResponse: (response: ActionResponse) => setResponse('export', response),
				execute: (params: ExportParams<any>, format:  ExportFormat) => execute('export', {params, format}),
			},

			generateAction: {
				response: responses.generate,
				setResponse: (response: ActionResponse) => setResponse('generate', response),
				execute: (params: ExportParams<any>, format:  ExportFormat) => execute('generate', {params, format}),
			},

			findListAction: {
				response: responses.findAllEntities,
				execute: () => execute('findAllEntities'),
			},
			findCurrentAction: {
				response: responses.findCurrentEntity,
				execute: () => execute('findCurrentEntity'),
			},


			/** Redux slice status & error */
			stateResponse : {
				status : entityModeStateStatus,
				error : entityModeState?.error
			},
			readStatus: entityModeState?.readStatus ?? false,
			curEntityMode,

			toggleEntityMode: () => dispatch(actionsRedux.toggleMode()),


			/** Store-only actions */
			addEntity: (entity: T) => execute('addEntity', entity),
			setEntity: (entity: T) => execute('setEntity', entity),
			removeEntity: (id: number | string) => execute('removeEntity', id),
			getEntity: (id: number | string) => execute('getEntity', id),
			getEntityList: () => execute('getEntityList'),
			clearData: () => execute('clearData'),
			setReadStatus: (isRead: boolean) => execute('setReadStatus', isRead),
		};
	};
}